/**
 * Pure, framework-free half of the shared "unread thread" home count (see
 * unreadHomeThreads.ts for the hook). Kept separate so it is testable
 * without Firebase or React, the same split staffWaitingOnYouCore.ts and
 * threadUnreadCount.ts already use.
 */

import { messageInboxStateKey, type MessageInboxStateDoc, type ThreadInboxActivityLike } from './messageInboxStateCore.ts';
import { threadUnreadSinceMs } from './threadUnreadCount.ts';

/**
 * Every thread id unread for `viewerUid`, by the inbox's OWN unread test
 * (threadUnreadSinceMs - the exact function the dot and the per-thread
 * badge already run), so a home count and the inbox can never disagree.
 * Takes already-fetched `threads`/`states` rather than fetching its own, so
 * a caller that already subscribes to both (every current caller does) does
 * not open a second, redundant Firestore listener for the same data.
 *
 * `mutedIds` is the viewer's own notifPrefs.mutedIds, passed straight
 * through to threadUnreadSinceMs so a fully muted admin board thread reads
 * the same way here as it does on the Messages row (2026-09-16 morning
 * batch item 2). Optional and defaulted to nothing muted, so every existing
 * caller that has not been given a mute list yet keeps its old behavior.
 */
export function unreadThreadIdsFrom(
  threads: readonly (ThreadInboxActivityLike & { id: string })[],
  states: ReadonlyMap<string, MessageInboxStateDoc>,
  viewerUid: string,
  mutedIds?: string[] | null
): Set<string> {
  return new Set(unreadThreadCursorsFrom(threads, states, viewerUid, mutedIds).keys());
}

/**
 * The same unread test as unreadThreadIdsFrom, keeping the read cursor each
 * unread thread counts from (threadUnreadSinceMs's own answer). A thread that
 * is not unread is absent. A value of 0 means never read, which is a real
 * cursor, so callers test with `has`, never truthiness.
 *
 * Why the cursor and not just the id: a recent @mention used to count as
 * waiting on you for seven days no matter what, so "Done with this" on an
 * Admin board reply that named her filed the row in Messages and left the
 * bell, the phone badge and the Messages menu badge saying one was still
 * waiting (Bobbie, 2026-09-24). A mention now counts only while it is newer
 * than this cursor, which is exactly while the Messages row still calls it
 * unread. See stillUnreadMentions in staffWaitingOnYouCore.ts.
 */
export function unreadThreadCursorsFrom(
  threads: readonly (ThreadInboxActivityLike & { id: string })[],
  states: ReadonlyMap<string, MessageInboxStateDoc>,
  viewerUid: string,
  mutedIds?: string[] | null
): Map<string, number> {
  const cursors = new Map<string, number>();
  if (!viewerUid) return cursors;
  for (const thread of threads) {
    const state = states.get(messageInboxStateKey('thread', thread.id));
    const since = threadUnreadSinceMs(thread, state, viewerUid, mutedIds);
    if (since !== null) cursors.set(thread.id, since);
  }
  return cursors;
}

/**
 * How many of `unreadThreadIds` are not already represented by some other
 * "waiting on you" source (an unacked must-read, a recent mention, a
 * notification doc already carrying its own card). Pass `scopeThreadIds`
 * to also require membership in a caller's own visible-thread set (e.g. the
 * participant-filtered list a home card already trusts); omit it to count
 * every unread thread.
 */
export function extraUnreadThreadCount(
  unreadThreadIds: ReadonlySet<string>,
  alreadyCountedThreadIds: ReadonlySet<string>,
  scopeThreadIds?: ReadonlySet<string>
): number {
  let count = 0;
  for (const threadId of unreadThreadIds) {
    if (scopeThreadIds && !scopeThreadIds.has(threadId)) continue;
    if (alreadyCountedThreadIds.has(threadId)) continue;
    count += 1;
  }
  return count;
}
